import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { ipoService } from '../../api/ipoService';
import { fetchAllIpoRecords } from '../../api/ipoCatalogPages';
import { mapWithConcurrency } from '../../api/mapWithConcurrency';
import { readBrokerReviewsCache, writeBrokerReviewsCache } from '../../api/brokerReviewsCache';
import { mapIpoDetailResponse } from '../../utils/ipoDataMapper';

/**
 * Just the catalog list (GET /api/core/ipos, every page): ids and names, for
 * finding an issue's catalog records without fetching each one's details.
 *
 * options.maxAgeMs: skip the request when data this recent is already loaded
 */
export const fetchCatalogIndex = createAsyncThunk(
  'ipoCatalog/fetchCatalogIndex',
  async (_options, { rejectWithValue }) => {
    try {
      const { records } = await fetchAllIpoRecords();
      return records;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to load the IPO catalog');
    }
  },
  {
    condition: ({ maxAgeMs = 0 } = {}, { getState }) => {
      const { status, fetchedAt } = getState().ipoCatalog.index;
      if (status === 'loading') return false;
      return !(maxAgeMs && status === 'succeeded' && fetchedAt && Date.now() - fetchedAt < maxAgeMs);
    },
  }
);

// Backs the Upcoming IPOs, Subscription Status and IPO Reviews pages, which
// (unlike the homepage's small "featured issues" carousel) need the *whole*
// published catalog to power their own client-side tabs/filters/CSV export.
// Bounded fan-out over GET /api/core/ipos/{id} rather than truly "all of
// them" -- fine for a one-time cached fetch, but not something to run
// unbounded as the catalog grows into the thousands.
const CATALOG_PAGE_SIZE = 200;
const CATALOG_DETAIL_CAP = 100;
// Requests in flight at once. The backend answers a burst of 100 with HTTP 429
// for most of them, so a fan-out that asks for less actually returns more.
const DETAIL_CONCURRENCY = 5;
const REVIEW_CONCURRENCY = 6;

export const fetchIpoCatalog = createAsyncThunk(
  'ipoCatalog/fetchIpoCatalog',
  async (_, { rejectWithValue }) => {
    try {
      const list = await ipoService.getIpos({ page: 1, page_size: CATALOG_PAGE_SIZE });
      const summaries = (list?.data || []).slice(0, CATALOG_DETAIL_CAP);
      const results = await mapWithConcurrency(summaries, (s) => ipoService.getIpoById(s.id), { limit: DETAIL_CONCURRENCY });
      const records = [];
      results.forEach((result) => {
        if (result.status !== 'fulfilled') return;
        const mapped = mapIpoDetailResponse(result.value);
        if (mapped) records.push(mapped);
      });
      return records;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to load IPO catalog');
    }
  }
);

/**
 * Broker research calls, one request per catalog id
 * (GET /api/core/ipos/{id}/broker-reviews).
 *
 * There is no bulk endpoint for mainboard issues -- /api/core/ipos/sme/reviews
 * exists but has no mainboard equivalent -- so this is a fan-out, and the only
 * thing that makes it safe is the concurrency ceiling: fired all at once the
 * backend rejects most of them and the page renders as if no broker had ever
 * published a call. Results are handed to the store as they land so rows fill
 * in while the rest are still coming. Answers are cached in the browser for
 * an hour, so a reload or a later visit asks only for what is missing.
 */
export const fetchCatalogReviews = createAsyncThunk(
  'ipoCatalog/fetchCatalogReviews',
  async (_options, { dispatch, getState, rejectWithValue }) => {
    try {
      const ids = getState().ipoCatalog.index.records.map((item) => item.id).filter((id) => id !== undefined);

      // Anything still fresh from a previous visit renders immediately and is
      // not requested again
      const cached = readBrokerReviewsCache();
      const restored = ids.filter((id) => cached[id] !== undefined);
      if (restored.length) {
        dispatch(catalogReviewsRestored(Object.fromEntries(restored.map((id) => [id, cached[id]]))));
      }

      const missing = ids.filter((id) => cached[id] === undefined);
      await mapWithConcurrency(missing, (id) => ipoService.getBrokerReviews(id), {
        limit: REVIEW_CONCURRENCY,
        onSettled: (result, id) => {
          if (result.status === 'fulfilled') dispatch(catalogReviewsReceived({ id, payload: result.value?.data || null }));
        },
      });

      // Ids the backend refused stay out of the cache, so the next visit retries them
      writeBrokerReviewsCache(getState().ipoCatalog.reviewsById);
      return { requested: missing.length, fromCache: restored.length };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to load broker reviews');
    }
  },
  {
    condition: (_options, { getState }) => {
      const { reviewsLoading, reviewsFetched, index } = getState().ipoCatalog;
      if (reviewsLoading || reviewsFetched) return false;
      // Nothing to fan out over until the list is actually in. Waiting on
      // fetchCatalogIndex is not enough: a second dispatch of it while the
      // first is in flight is skipped and resolves at once, so under
      // StrictMode's double effect this would otherwise run against an empty
      // list and mark itself done.
      return index.records.length > 0;
    },
  }
);

const initialState = {
  records: [],
  recordsLoading: false,
  recordsError: null,
  recordsFetched: false,

  reviewsById: {},
  reviewsLoading: false,
  reviewsError: null,
  reviewsFetched: false,

  // fetchCatalogIndex: raw list items, kept through a failed refresh
  index: { records: [], fetchedAt: null, status: 'idle', error: null },
};

export const ipoCatalogSlice = createSlice({
  name: 'ipoCatalog',
  initialState,
  reducers: {
    // One broker-review response, folded in as it arrives
    catalogReviewsReceived: (state, action) => {
      state.reviewsById[action.payload.id] = action.payload.payload;
    },
    // A batch lifted straight out of the browser cache
    catalogReviewsRestored: (state, action) => {
      state.reviewsById = { ...state.reviewsById, ...action.payload };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchIpoCatalog.pending, (state) => {
        state.recordsLoading = true;
        state.recordsError = null;
      })
      .addCase(fetchIpoCatalog.fulfilled, (state, action) => {
        state.recordsLoading = false;
        state.recordsFetched = true;
        state.records = action.payload;
      })
      .addCase(fetchIpoCatalog.rejected, (state, action) => {
        state.recordsLoading = false;
        state.recordsFetched = true;
        state.recordsError = action.payload || 'Failed to load IPO catalog';
      })

      .addCase(fetchCatalogReviews.pending, (state) => {
        state.reviewsLoading = true;
        state.reviewsError = null;
      })
      .addCase(fetchCatalogReviews.fulfilled, (state) => {
        // Each response was already folded in by catalogReviewsReceived
        state.reviewsLoading = false;
        state.reviewsFetched = true;
      })
      .addCase(fetchCatalogReviews.rejected, (state, action) => {
        state.reviewsLoading = false;
        state.reviewsFetched = true;
        state.reviewsError = action.payload || 'Failed to load broker reviews';
      })

      .addCase(fetchCatalogIndex.pending, (state) => {
        state.index.status = 'loading';
        state.index.error = null;
      })
      .addCase(fetchCatalogIndex.fulfilled, (state, action) => {
        state.index = { records: action.payload, fetchedAt: Date.now(), status: 'succeeded', error: null };
      })
      .addCase(fetchCatalogIndex.rejected, (state, action) => {
        state.index.status = 'failed';
        state.index.error = action.payload || 'Failed to load the IPO catalog';
      });
  },
});

export const { catalogReviewsReceived, catalogReviewsRestored } = ipoCatalogSlice.actions;

export default ipoCatalogSlice.reducer;
